// Utility function to remove undefined values from objects
export const removeUndefinedValues = (obj) => {
  const cleaned = {};
  
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    
    if (value !== undefined && value !== null) {
      if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        // Recursively clean nested objects
        const cleanedNested = removeUndefinedValues(value);
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
      } else {
        cleaned[key] = value;
      }
    }
  });
  
  return cleaned;
};

// Validate profile completeness
export const validateProfileData = (profile) => {
  const errors = {};
  
  if (!profile) {
    errors.general = 'Profile data is required';
    return { isValid: false, errors };
  }
  
  // Check required fields
  const requiredFields = {
    fullName: 'Full name',
    email: 'Email address',
    county: 'County',
    mpesaNumber: 'M-Pesa number',
    nationalId: 'National ID',
    gender: 'Gender',
    dateOfBirth: 'Date of birth',
    maritalStatus: 'Marital status',
    education: 'Education level',
    employmentStatus: 'Employment status',
    monthlyIncome: 'Monthly income'
  };
  
  Object.keys(requiredFields).forEach(field => {
    if (!profile[field] || profile[field] === '') {
      errors[field] = `${requiredFields[field]} is required`;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Keep M-Pesa number in local format - no conversion needed
export const normalizeMpesaNumber = (number) => {
  if (!number) return '';
  
  // Remove all spaces and non-digit characters
  const cleanNumber = number.replace(/\D/g, '');
  
  // Return as-is if it's valid local format (07 or 01)
  if (/^0[17]\d{8}$/.test(cleanNumber)) {
    return cleanNumber;
  }
  
  // Return the original if it doesn't match expected format
  return cleanNumber;
};

// Format M-Pesa number for display - keep in local format
export const formatMpesaNumberForDisplay = (number) => {
  if (!number) return '';
  
  // Return the number as-is since we're keeping local format
  return number;
};

// Validate M-Pesa message format - Simplified error message
export const validateMpesaMessage = (message, expectedAmount, tillName) => {
  const errors = {};

  if (!message || !message.trim()) {
    errors.message = 'M-Pesa message is required';
    return { isValid: false, errors };
  }

  // Extract M-Pesa code (first 10 characters)
  const mpesaCode = message.substring(0, 10);
  if (mpesaCode.length !== 10) {
    errors.message = `Incorrect payment. Make a new payment of KES ${expectedAmount} to Till Number 5678901`;
    return { isValid: false, errors };
  }

  // Check if message contains till name
  const containsTillName = message.toUpperCase().includes(tillName.toUpperCase());
  if (!containsTillName) {
    errors.message = `Incorrect payment. Make a new payment of KES ${expectedAmount} to Till Number 5678901`;
    return { isValid: false, errors };
  }

  // Check if message contains the correct amount
  const amountRegex = new RegExp(`${expectedAmount}(?:\\.00)?`, 'i');
  const containsAmount = amountRegex.test(message);
  if (!containsAmount) {
    errors.message = `Incorrect payment. Make a new payment of KES ${expectedAmount} to Till Number 5678901`;
    return { isValid: false, errors };
  }

  return { isValid: true, errors: {}, mpesaCode };
};

// Validate email format
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate M-Pesa number format - Updated to accept local format
export const validateMpesaNumber = (number) => {
  if (!number) return false;
  
  const cleanNumber = number.replace(/\D/g, '');
  
  // Accept local format (07XXXXXXXX or 01XXXXXXXX) - no 254 conversion
  return /^0[17]\d{8}$/.test(cleanNumber);
};

// Validate National ID format
export const validateNationalId = (id) => {
  return /^\d{8}$/.test(id);
};

// Validate PIN format
export const validatePin = (pin) => {
  return /^\d{6}$/.test(pin);
};